import { useCallback, useEffect, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Toaster, toast } from "sonner";
import { api, formatApiError } from "../../lib/apiClient";
import ImageUpload from "../../components/ImageUpload";

const TYPES = [
    { value: "cookie", label: "Cookie" },
    { value: "brownie", label: "Brownie" },
    { value: "drink", label: "Boisson" },
];
const CATS = ["classique", "croquant", "gourmand", "premium"];
const DRINK_CATS = [
    { value: "cafe", label: "Café" },
    { value: "soft_artisanal", label: "Softs artisanaux" },
    { value: "soft", label: "Softs" },
];

// Match the public Carte order: cookies (by category) → brownies → drinks (by drink_category)
const TYPE_ORDER = { cookie: 0, brownie: 1, drink: 2 };
const CATEGORY_ORDER_IDX = { classique: 0, croquant: 1, gourmand: 2, premium: 3 };
const DRINK_CAT_ORDER_IDX = { cafe: 0, soft_artisanal: 1, soft: 2 };
function sortLikeCarte(a, b) {
    const t = (TYPE_ORDER[a.item_type] ?? 99) - (TYPE_ORDER[b.item_type] ?? 99);
    if (t !== 0) return t;
    if (a.item_type === "cookie") {
        const c = (CATEGORY_ORDER_IDX[a.category] ?? 99) - (CATEGORY_ORDER_IDX[b.category] ?? 99);
        if (c !== 0) return c;
    }
    if (a.item_type === "drink") {
        const c = (DRINK_CAT_ORDER_IDX[a.drink_category] ?? 99) - (DRINK_CAT_ORDER_IDX[b.drink_category] ?? 99);
        if (c !== 0) return c;
    }
    return (a.sort_order ?? 0) - (b.sort_order ?? 0);
}

const EMPTY = {
    name: "",
    description: "",
    price: "",
    item_type: "cookie",
    category: "classique",
    drink_category: "cafe",
    image_url: "",
    available: true,
    available_for_order: true,
    is_cookie_of_month: false,
    sort_order: 0,
};

export default function MenuManager() {
    const [items, setItems] = useState([]);
    const [prices, setPrices] = useState({ classique: 2.5, croquant: 3, gourmand: 3.4, premium: 3.7 });
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);

    const fetchAll = useCallback(async () => {
        try {
            const [m, s] = await Promise.all([
                api.get("/admin/menu"),
                api.get("/admin/settings"),
            ]);
            setItems(m.data);
            if (s.data?.category_prices) setPrices(s.data.category_prices);
        } catch (_e) {
            toast.error("Erreur de chargement");
        }
    }, []);

    useEffect(() => {
        fetchAll();
    }, [fetchAll]);

    const startEdit = (it) => {
        setEditing(it.id);
        setForm({
            name: it.name,
            description: it.description || "",
            price: it.price ?? "",
            item_type: it.item_type,
            category: it.category || "classique",
            image_url: it.image_url || "",
            available: it.available,
            available_for_order: it.available_for_order,
            is_cookie_of_month: it.is_cookie_of_month,
            sort_order: it.sort_order || 0,
        });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const reset = () => {
        setEditing(null);
        setForm(EMPTY);
    };

    const save = async (e) => {
        e.preventDefault();
        const payload = {
            ...form,
            price: form.price === "" ? null : Number(form.price),
            sort_order: Number(form.sort_order) || 0,
            category: form.item_type === "cookie" ? form.category : null,
        };
        try {
            if (editing) {
                await api.put(`/admin/menu/${editing}`, payload);
                toast.success("Article mis à jour");
            } else {
                await api.post("/admin/menu", payload);
                toast.success("Article créé");
            }
            reset();
            fetchAll();
        } catch (e2) {
            toast.error(formatApiError(e2.response?.data?.detail) || "Erreur");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Supprimer cet article ?")) return;
        try {
            await api.delete(`/admin/menu/${id}`);
            toast.success("Supprimé");
            fetchAll();
        } catch (_e) {
            toast.error("Erreur");
        }
    };

    const savePrices = async (e) => {
        e.preventDefault();
        try {
            await api.put(
                "/admin/settings/category-prices",
                CATS.map((c) => ({ category: c, price: Number(prices[c]) }))
            );
            toast.success("Prix mis à jour");
            fetchAll();
        } catch (_err) {
            toast.error("Erreur");
        }
    };

    return (
        <div data-testid="admin-menu-page">
            <Toaster richColors position="top-center" />
            <p className="section-tag">administration</p>
            <h1 className="font-serif-display text-4xl text-[#2A1408] tracking-tight">
                Carte
            </h1>

            <section className="brand-card p-6 mt-6">
                <h2 className="font-serif-display text-2xl text-[#2A1408] mb-4">
                    Prix par catégorie de cookies
                </h2>
                <form onSubmit={savePrices} className="grid sm:grid-cols-4 gap-3">
                    {CATS.map((c) => (
                        <label key={c} className="block text-xs uppercase tracking-widest text-[#5C3D2E]">
                            {c}
                            <input
                                type="number"
                                step="0.01"
                                value={prices[c] ?? ""}
                                onChange={(ev) =>
                                    setPrices((p) => ({ ...p, [c]: ev.target.value }))
                                }
                                className="mt-1 w-full bg-white border border-[#E4D1BB] rounded-md px-3 py-2 text-sm"
                                data-testid={`price-${c}`}
                            />
                        </label>
                    ))}
                    <div className="sm:col-span-4">
                        <button type="submit" className="brand-btn" data-testid="save-prices">
                            Enregistrer les prix
                        </button>
                    </div>
                </form>
            </section>

            <section className="brand-card p-6 mt-6">
                <h2 className="font-serif-display text-2xl text-[#2A1408] mb-4">
                    {editing ? "Modifier l’article" : "Nouvel article"}
                </h2>
                <form onSubmit={save} className="grid md:grid-cols-2 gap-4" data-testid="menu-form">
                    <label className="text-xs uppercase tracking-widest text-[#5C3D2E]">
                        Nom
                        <input
                            required
                            value={form.name}
                            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                            className="mt-1 w-full bg-white border border-[#E4D1BB] rounded-md px-3 py-2 text-sm"
                            data-testid="menu-name"
                        />
                    </label>
                    <label className="text-xs uppercase tracking-widest text-[#5C3D2E]">
                        Type
                        <select
                            value={form.item_type}
                            onChange={(e) => setForm((f) => ({ ...f, item_type: e.target.value }))}
                            className="mt-1 w-full bg-white border border-[#E4D1BB] rounded-md px-3 py-2 text-sm"
                            data-testid="menu-type"
                        >
                            {TYPES.map((t) => (
                                <option key={t.value} value={t.value}>
                                    {t.label}
                                </option>
                            ))}
                        </select>
                    </label>
                    {form.item_type === "cookie" && (
                        <label className="text-xs uppercase tracking-widest text-[#5C3D2E]">
                            Catégorie
                            <select
                                value={form.category}
                                onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                                className="mt-1 w-full bg-white border border-[#E4D1BB] rounded-md px-3 py-2 text-sm"
                                data-testid="menu-category"
                            >
                                {CATS.map((c) => (
                                    <option key={c} value={c}>
                                        {c}
                                    </option>
                                ))}
                            </select>
                        </label>
                    )}
                    {form.item_type === "drink" && (
                        <label className="text-xs uppercase tracking-widest text-[#5C3D2E]">
                            Sous-catégorie boisson
                            <select
                                value={form.drink_category || "cafe"}
                                onChange={(e) => setForm((f) => ({ ...f, drink_category: e.target.value }))}
                                className="mt-1 w-full bg-white border border-[#E4D1BB] rounded-md px-3 py-2 text-sm"
                                data-testid="menu-drink-category"
                            >
                                {DRINK_CATS.map((c) => (
                                    <option key={c.value} value={c.value}>
                                        {c.label}
                                    </option>
                                ))}
                            </select>
                        </label>
                    )}
                    <label className="text-xs uppercase tracking-widest text-[#5C3D2E]">
                        Prix (€)
                        <input
                            type="number"
                            step="0.01"
                            value={form.price}
                            onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
                            className="mt-1 w-full bg-white border border-[#E4D1BB] rounded-md px-3 py-2 text-sm"
                            data-testid="menu-price"
                        />
                    </label>
                    <label className="md:col-span-2 text-xs uppercase tracking-widest text-[#5C3D2E]">
                        Description
                        <textarea
                            rows={2}
                            value={form.description}
                            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                            className="mt-1 w-full bg-white border border-[#E4D1BB] rounded-md px-3 py-2 text-sm"
                            data-testid="menu-desc"
                        />
                    </label>
                    <label className="md:col-span-2 text-xs uppercase tracking-widest text-[#5C3D2E]">
                        Photo (carré 1:1 recommandé)
                        <div className="mt-1">
                            <ImageUpload
                                value={form.image_url}
                                onChange={(url) => setForm((f) => ({ ...f, image_url: url }))}
                                label=""
                            />
                        </div>
                    </label>
                    <label className="text-xs uppercase tracking-widest text-[#5C3D2E]">
                        Ordre d’affichage
                        <input
                            type="number"
                            value={form.sort_order}
                            onChange={(e) => setForm((f) => ({ ...f, sort_order: e.target.value }))}
                            className="mt-1 w-full bg-white border border-[#E4D1BB] rounded-md px-3 py-2 text-sm"
                        />
                    </label>
                    <div className="flex items-end">
                        <label className="flex items-center gap-2 text-sm text-[#5C3D2E]">
                            <input
                                type="checkbox"
                                checked={form.available}
                                onChange={(e) => setForm((f) => ({ ...f, available: e.target.checked }))}
                            />
                            Visible sur la carte
                        </label>
                    </div>
                    <div className="md:col-span-2 flex gap-3">
                        <button type="submit" className="brand-btn" data-testid="menu-submit">
                            <Plus size={16} className="inline mr-1" />
                            {editing ? "Mettre à jour" : "Ajouter"}
                        </button>
                        {editing && (
                            <button
                                type="button"
                                onClick={reset}
                                className="brand-btn-outline"
                                data-testid="menu-cancel"
                            >
                                Annuler
                            </button>
                        )}
                    </div>
                </form>
            </section>

            <section className="mt-8">
                <h2 className="font-serif-display text-2xl text-[#2A1408] mb-4">
                    Tous les articles ({items.length})
                </h2>
                {(() => {
                    const sorted = [...items].sort(sortLikeCarte);
                    const groups = [
                        { key: "cookie-classique", title: "Cookies, classiques", filter: (i) => i.item_type === "cookie" && i.category === "classique" },
                        { key: "cookie-croquant", title: "Cookies, croquants", filter: (i) => i.item_type === "cookie" && i.category === "croquant" },
                        { key: "cookie-gourmand", title: "Cookies, gourmands", filter: (i) => i.item_type === "cookie" && i.category === "gourmand" },
                        { key: "cookie-premium", title: "Cookies, premiums", filter: (i) => i.item_type === "cookie" && i.category === "premium" },
                        { key: "brownie", title: "Brownies", filter: (i) => i.item_type === "brownie" },
                        { key: "drink-cafe", title: "Boissons, café", filter: (i) => i.item_type === "drink" && i.drink_category === "cafe" },
                        { key: "drink-soft-art", title: "Boissons, softs artisanaux", filter: (i) => i.item_type === "drink" && i.drink_category === "soft_artisanal" },
                        { key: "drink-soft", title: "Boissons, softs", filter: (i) => i.item_type === "drink" && i.drink_category === "soft" },
                        { key: "drink-other", title: "Boissons, non classées", filter: (i) => i.item_type === "drink" && !["cafe", "soft_artisanal", "soft"].includes(i.drink_category) },
                        { key: "other", title: "Autres", filter: (i) => !["cookie", "brownie", "drink"].includes(i.item_type) },
                    ];
                    return groups
                        .map((g) => ({ ...g, items: sorted.filter(g.filter) }))
                        .filter((g) => g.items.length > 0)
                        .map((g) => (
                            <div key={g.key} className="mb-8">
                                <div className="flex items-center gap-3 mb-3">
                                    <h3 className="font-display text-xl text-[#2A1408] uppercase tracking-wider">
                                        {g.title}
                                    </h3>
                                    <span className="text-xs text-[#B07B66]">({g.items.length})</span>
                                    <div className="flex-1 h-px bg-[#E4D1BB]" />
                                </div>
                                <div className="grid md:grid-cols-2 gap-4">
                                    {g.items.map((it) => (
                                        <article
                                            key={it.id}
                                            className="brand-card p-5 flex justify-between gap-3"
                                            data-testid={`admin-item-${it.id}`}
                                        >
                                            <div className="min-w-0">
                                                {it.is_cookie_of_month && (
                                                    <p className="text-xs uppercase tracking-widest text-[#D6552C] font-bold">
                                                        ⭐ cookie du mois
                                                    </p>
                                                )}
                                                <p className="font-serif-display text-lg text-[#2A1408] truncate">
                                                    {it.name}
                                                </p>
                                                <p className="text-sm text-[#5C3D2E] line-clamp-2">
                                                    {it.description}
                                                </p>
                                                <p className="text-sm mt-1 text-[#3D1F0F]">
                                                    {it.price != null ? `${Number(it.price).toFixed(2).replace(".", ",")} €` : "-"}
                                                </p>
                                            </div>
                                            <div className="flex flex-col gap-2 shrink-0">
                                                <button
                                                    type="button"
                                                    onClick={() => startEdit(it)}
                                                    className="text-[#6B4226] hover:text-[#3D1F0F]"
                                                    aria-label="Modifier"
                                                    data-testid={`edit-${it.id}`}
                                                >
                                                    <Pencil size={16} />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => remove(it.id)}
                                                    className="text-red-700 hover:text-red-900"
                                                    aria-label="Supprimer"
                                                    data-testid={`del-${it.id}`}
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </article>
                                    ))}
                                </div>
                            </div>
                        ));
                })()}
            </section>
        </div>
    );
}
